import React from 'react';
import Grid from "@mui/material/Grid";
import FormModal from '../../../components/FormModal';
import Button from "@mui/material/Button";
import { postApi, getAPIDataParamsReturn } from '../../../utils/apiFunction';
import { useSearchParams } from "react-router-dom";
import { ACCESS, ACCESS_SECTIONS } from '../../../utils/constants/access';
import OnShoreCard from './OnShoreCard';
import HistoryDisplay from './HistoryDisplay';
import { Box, CircularProgress, Typography, TextField, Switch, FormControlLabel, List, ListItem, ListItemText, Divider, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { dateFormat } from "../../../utils/dateFunctions";
import Div from "@jumbo/shared/Div";
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import { checkAccess } from '../../../utils/roleFunctions';
import SubmitButtonDisplayer from "../../../components/SubmitButtonDisplayer";

const READY = 'Ready';
const PICKEDUP = 'PickedUp';
const RECEIVED = 'Received';
const CANCELLED = 'Cancelled';

const getGroups = async (propertyGroups) => {
    console.log("getGroups", propertyGroups);
    var groups = [];
    groups = propertyGroups.map((group) => {
        return getAPIDataParamsReturn('/Farms/Groups/GetGroup', { Id: group?.row?.props?.GroupId }).then((res) => {
            return res;
        });
    }
    );
    await Promise.all(groups).then((res) => {
        groups = res;
    });
    return groups;
}

const NoteEdit = (props) => {
    const [groups, setGroups] = React.useState(null);
    const [completed, setCompleted] = React.useState(props.completed);
    const [loading, setLoading] = React.useState(false);
    // noteEdits: { [groupIndex]: { byId: { [noteId]: string }, unlocked: { [noteId]: boolean } } }
    const [noteEdits, setNoteEdits] = React.useState({});
    // newNotes: { [groupIndex]: string }
    const [newNotes, setNewNotes] = React.useState({});
    const [error, setError] = React.useState(null);
    const Swal = useSwalWrapper();
    const requiredEditAccess = { key: ACCESS_SECTIONS.FARMS, value: ACCESS.EDIT };

    React.useEffect(() => {
        const primeState = (list) => {
            // Build initial note edits and newNotes from groups with OCNotes
            const edits = {};
            const news = {};
            list.forEach((g, gi) => {
                const byId = {};
                const unlocked = {};
                // sort notes ascending by created for consistent UI
                const ocNotes = (g.ocNotes || []).slice().sort((a, b) => new Date(a.created) - new Date(b.created));
                ocNotes.forEach(n => {
                    byId[n.id] = n.note ?? '';
                    unlocked[n.id] = false; // default locked
                });
                edits[gi] = { byId, unlocked, original: ocNotes };
                news[gi] = '';
            });
            setNoteEdits(edits);
            setNewNotes(news);
        }

        if (props.group) {
            const list = [props.group];
            setGroups(list);
            primeState(list);
        } else {
            getGroups(props.propertyGroups).then((res) => {
                setGroups(res);
                primeState(res);
            });
        }

    }, [props.group, props.propertyGroups]);

    const validate = () => {
        var warningMessages = [];
        var errorMessages = [];

        const formatErrorMessage = (array) => {
            var errString = '';
            array.map((a) => {
                errString = errString + a + '<br/>';
            });
            return errString;
        }
        if (errorMessages.length > 0) {
            Swal.fire({
                html: formatErrorMessage(errorMessages),
                icon: 'error',
                title: 'Oops...',

            });
        } else if (warningMessages.length > 0) {
            Swal.fire({
                html: formatErrorMessage(warningMessages),
                icon: 'warning',
                title: 'Oops...',
                showCancelButton: true,
                confirmButtonText: 'Yes, Continue',
                cancelButtonText: 'No, I\'ll edit it',

            }).then(result => {
                if (result.value) {
                    submit();
                } else if (result.dismiss === Swal.DismissReason.cancel) {

                }
            });
        } else {
            submit();
        }
    }
    const toggleUnlock = (groupIndex, noteId) => (event) => {
        setNoteEdits(prev => ({
            ...prev,
            [groupIndex]: {
                ...prev[groupIndex],
                unlocked: { ...prev[groupIndex].unlocked, [noteId]: event.target.checked }
            }
        }));
    };

    const updateNoteValue = (groupIndex, noteId) => (event) => {
        const value = event.target.value;
        setNoteEdits(prev => ({
            ...prev,
            [groupIndex]: {
                ...prev[groupIndex],
                byId: { ...prev[groupIndex].byId, [noteId]: value }
            }
        }));
    };

    const updateNewNote = (groupIndex) => (event) => {
        const value = event.target.value;
        setNewNotes(prev => ({ ...prev, [groupIndex]: value }));
    };
    const submit = async () => {
        setLoading(true);
        setCompleted(true);
        let lastRes = { ok: true };
        // Process edits for each group
        for (let gi = 0; gi < (groups?.length || 0); gi++) {
            const g = groups[gi];
            const edits = noteEdits[gi];
            if (edits) {
                const originals = edits.original || [];
                for (const orig of originals) {
                    const id = orig.id;
                    const unlocked = edits.unlocked[id];
                    const newVal = edits.byId[id];
                    if (unlocked && newVal !== (orig.note ?? '')) {
                        const res = await postApi('/Farms/Groups/Notes/Update', { Id: id, Note: newVal });
                        lastRes = res;
                    }
                }
            }
            // Create new note if provided
            const nn = newNotes[gi];
            if (nn && nn.trim().length > 0) {
                const res = await postApi('/Farms/Groups/Notes/Create', { GroupId: g?.id, Note: nn.trim() });
                lastRes = res;
            }
        }
        if (!lastRes.ok) {
            setError("Error updating notes");
        } else {
            setCompleted(true);
        }
        if (props.reload) {
            await props.reload();
        }
        setLoading(false);

    }
    const deleteNote = async (groupIndex, noteId) => {
        const allowed = checkAccess(props.access, requiredEditAccess);
        if (!allowed || completed) return;
        const confirm = await Swal.fire({
            title: 'Delete note?',
            text: 'This cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });
        if (!confirm.value) return;
        const res = await postApi('/Farms/Groups/Notes/Delete', { Id: noteId });
        if (!res.ok) {
            Swal.fire({ title: 'Error', text: 'Failed to delete note', icon: 'error' });
            return;
        }
        // Remove from local state
        setNoteEdits(prev => {
            const next = { ...prev };
            const groupState = next[groupIndex];
            if (groupState) {
                const { byId, unlocked, original } = groupState;
                const newById = { ...byId };
                const newUnlocked = { ...unlocked };
                delete newById[noteId];
                delete newUnlocked[noteId];
                const newOriginal = (original || []).filter(o => o.id !== noteId);
                next[groupIndex] = { byId: newById, unlocked: newUnlocked, original: newOriginal };
            }
            return next;
        });
        setGroups(prev => {
            if (!prev) return prev;
            const next = [...prev];
            const g = next[groupIndex];
            if (g) {
                g.ocNotes = (g.ocNotes || []).filter(n => n.id !== noteId);
            }
            return next;
        });
    }
    return (
        <Box
            component="form"
            sx={{
                '& .MuiTextField-root': { m: 1, width: '24ch' },
                zIndex: '1005',

            }}
            noValidate
            autoComplete="off"
        >
            <Div>

                <Typography variant={"h2"} >Notes</Typography>
                <Grid container spacing={2.5} mb={1}>

                    {groups ? groups.map((group, ind) => {
                        return <React.Fragment key={ind}>
                            <Grid item xs={12} sm={12} md={6} >
                                <OnShoreCard StillOnWater={!group?.shore} cropCols={props.cropCols} isSel={false} selectedCard={() => { }} selected={null} group={group} canCombine={false} opacity={0.5} ind={0} selectFunc={() => { }} combineFunc={() => { }} gradeFunc={() => { }} splitFunc={() => { }} />
                            </Grid>
                            <Grid item xs={12} sm={12} md={5}>
                                {/* Existing notes list (tidy view) */}
                                <List dense sx={{ width: '100%', bgcolor: 'background.paper', border: '1px solid #eee', borderRadius: 1 }}>
                                    {(group?.ocNotes || [])
                                        .slice()
                                        .sort((a, b) => new Date(a.created) - new Date(b.created))
                                        .map((n, idx) => {
                                            const unlocked = noteEdits?.[ind]?.unlocked?.[n.id] || false;
                                            const val = noteEdits?.[ind]?.byId?.[n.id] ?? '';
                                            const dateStr = dateFormat(new Date(n.created));
                                            return (
                                                <React.Fragment key={`note-${n.id}`}>
                                                    <ListItem alignItems="flex-start" sx={{ py: 1.5, px: 2 }}
                                                        secondaryAction={unlocked ? (
                                                            <Tooltip title={completed ? 'Completed' : 'Delete'}>
                                                                <span>
                                                                    <IconButton
                                                                        edge="end"
                                                                        aria-label="delete"
                                                                        onClick={() => deleteNote(ind, n.id)}
                                                                        disabled={completed}
                                                                        sx={{ ml: 1.5, mr: 1.5, mt: 0.5 }}
                                                                        size="large"
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        ) : null}
                                                    >
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{dateStr}</Typography>
                                                            {unlocked ? (
                                                                <TextField
                                                                    autoFocus
                                                                    disabled={completed}
                                                                    fullWidth
                                                                    multiline
                                                                    minRows={2}
                                                                    sx={{ mt: 0.5 }}
                                                                    value={val}
                                                                    onChange={updateNoteValue(ind, n.id)}
                                                                />
                                                            ) : (
                                                                <ListItemText
                                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                                    sx={{ mt: 0.5 }}
                                                                    primary={val || ''}
                                                                />
                                                            )}
                                                        </Box>
                                                        <FormControlLabel
                                                            sx={{ ml: 2 }}
                                                            control={<Switch checked={unlocked} onChange={toggleUnlock(ind, n.id)} />}
                                                            label="Unlock"
                                                        />
                                                    </ListItem>
                                                    {idx < (group?.ocNotes?.length || 0) - 1 && <Divider component="li" />}
                                                </React.Fragment>
                                            );
                                        })}
                                </List>

                                {/* New note field */}
                                <Typography variant="h6" sx={{ mt: 2 }}>Add New Note</Typography>
                                <TextField
                                    disabled={completed}
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label="New note"
                                    style= {{ width: '100%' }}
                                    value={newNotes[ind] || ''}
                                    onChange={updateNewNote(ind)}
                                />
                            </Grid>
                        </React.Fragment>
                    }) : <CircularProgress />}
                        <Grid item xs={12}  >
                            <SubmitButtonDisplayer error={error} title={'Submit'} completed={completed} loading={loading} allowed={checkAccess(props.access, requiredEditAccess)} submitFunc={validate} handleClose={props.handleClose} />
                        </Grid>
                    </Grid>
                

            </Div>
        </Box >
    );
}
export default NoteEdit;
